import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { formatApiError } from "@/lib/api";

/** The six-field line every Simple product page builds (1.4): note, need-by, cleanup, customer-supplied, then Pay now / Add to cart. */
export function useSimpleLine({ store, cleanupFee }) {
    const [note, setNote] = useState("");
    const [needBy, setNeedBy] = useState("");
    const [cleanup, setCleanup] = useState(false);
    const [supplied, setSupplied] = useState(false);
    const [ack, setAck] = useState(false);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { addItem, removeItem, setOpen, items } = useCart();

    const fee = cleanup ? Number(cleanupFee || 0) : 0;
    const extraDays = cleanup || supplied ? 1 : 0;
    const home = store?.slug && store.slug !== "13th-ave-sign-and-shirt" ? `/stores/${store.slug}` : "/";
    const editId = location.state?.editId || null;   // Edit from the cart (7.6): the new line replaces the old one

    const decorate = (item, key) => ({
        ...item,
        line_total: Math.round((Number(item.line_total || 0) + fee) * 100) / 100,
        summary: [item.summary, cleanup ? "artwork cleanup" : null, supplied ? "customer-supplied" : null, note ? `“${note.slice(0, 60)}”` : null].filter(Boolean).join(" · "),
        edit_path: `${location.pathname}${location.search}`,
        selections: {
            ...item.selections, note: note.trim(), need_by: needBy || undefined, cleanup,
            customer_supplied: supplied || undefined, source: supplied ? "supplied" : item.selections?.source,
            dedupe_key: key || undefined,
        },
    });

    /** The same configuration is already in the cart (so Pay now should not add it twice). */
    const inCart = (key) => !!key && items.some((i) => i.selections?.dedupe_key === key);

    /** `build` returns the cart item; "pay" goes straight to the register, "drawer" opens the cart (the designers), "cart" goes back to the grid. */
    const submit = async (build, mode, key) => {
        setBusy(true);
        try {
            const item = decorate(await build(), key);
            if (editId) removeItem(editId);
            addItem(item);
            if (mode === "pay") navigate(`/checkout/${item.selections.store_slug || store?.slug}`);
            else if (mode === "drawer") { toast.success("Added to cart"); setOpen(true); }
            else navigate(editId ? "/cart" : home, { state: { added: true } });
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Something went wrong. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const suppliedError = supplied && !ack ? "Tick the customer-supplied box to continue" : null;
    return { note, setNote, needBy, setNeedBy, cleanup, setCleanup, supplied, setSupplied, ack, setAck, busy, fee, extraDays, submit, inCart, suppliedError, editing: !!editId };
}
